import * as React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-4 pointer-events-auto bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center mb-2",
        caption_label: "text-base font-semibold text-slate-800 dark:text-slate-100",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          "h-8 w-8 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg flex items-center justify-center transition-colors",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse",
        head_row: "flex mb-1",
        head_cell: "text-slate-500 dark:text-slate-400 rounded-md w-10 font-medium text-xs uppercase",
        row: "flex w-full mt-1",
        cell: cn(
          "h-10 w-10 text-center text-sm p-0 relative",
          "[&:has([aria-selected].day-range-end)]:rounded-r-lg",
          "[&:has([aria-selected].day-outside)]:bg-blue-50/50 dark:[&:has([aria-selected].day-outside)]:bg-blue-900/20",
          "[&:has([aria-selected])]:bg-blue-50 dark:[&:has([aria-selected])]:bg-blue-900/30",
          "first:[&:has([aria-selected])]:rounded-l-lg last:[&:has([aria-selected])]:rounded-r-lg",
          "focus-within:relative focus-within:z-20"
        ),
        day: cn(
          "h-10 w-10 p-0 font-normal rounded-lg transition-all duration-200",
          "hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:text-blue-700 dark:hover:text-blue-300",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
          "aria-selected:opacity-100"
        ),
        day_range_end: "day-range-end",
        day_selected: cn(
          "bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-semibold shadow-md",
          "hover:from-blue-600 hover:to-indigo-700 hover:text-white",
          "focus:from-blue-600 focus:to-indigo-700 focus:text-white"
        ),
        day_today: cn(
          "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold",
          "ring-2 ring-blue-500/30"
        ),
        day_outside: cn(
          "day-outside text-slate-300 dark:text-slate-600 opacity-50",
          "aria-selected:bg-blue-50/50 dark:aria-selected:bg-blue-900/20",
          "aria-selected:text-slate-400 dark:aria-selected:text-slate-500 aria-selected:opacity-30"
        ),
        day_disabled: "text-slate-300 dark:text-slate-600 opacity-40 cursor-not-allowed hover:bg-transparent",
        day_range_middle: "aria-selected:bg-blue-50 dark:aria-selected:bg-blue-900/30 aria-selected:text-blue-700 dark:aria-selected:text-blue-300",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-5 w-5 text-slate-600 dark:text-slate-300" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-5 w-5 text-slate-600 dark:text-slate-300" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
